import React, { useEffect, useState, forwardRef } from "react";
import "../components/SideTile.css";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getSectionStatus } from "../apis/SzakokFinder";

const SideTile = forwardRef(({ id: szakId, texts, isVisible, preview = false }, ref) => {  // isVisible prop hozzáadása
  const [statuses, setStatuses] = useState({});
  const [isOpen, setIsOpen] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const contentRef = React.useRef(null);



  const textToStatusKeyMap = {
    // Hungarian texts
    "Szak alapadatok": "Szakjelentés alapadatok",
    "A képzés": "Képzés célja",
    "A szak mintatanterve": "Szak mintaterve",
    "Tehetséges, kiemelkedő hallgatói teljesítmények": "Tehetséges hallgatók",
    "Idegen nyelvi követelmények": "Idegen nyelvi követelmények",
    "Kötelező szakmai gyakorlat": "Kötelező szakmai gyakorlat",
    "Mintatanterv fejlesztések, módosítások": "Mintatanterv fejlesztések, módosítások",
    "Fejlesztési javaslatok": "Fejlesztési javaslatok",
    "Oktatás": "Oktatás",
    "A szak hallgatóira vonatkozó adatok": "Szak létszám adatok",
    "Versenytárs elemzés": "Versenytárs elemzés",
    "Meghívott előadók és óraadók": "Meghívott előadók és óraadók",
    "Oktatók értékelése": "Oktatók értékelése",
    "A képzés tanulmányi körülményei": "Tanulmányi körülmények",
    "A szak kommunikációja": "Szak kommunikációja",
    "Fejlesztési javaslatok megvalósítása és értékelése": "Fejlesztési javaslatok megvalósítása és értékelése",
    "Szakjelentés lezárása": "Szakjelentés alapadatok",
    
    // English texts (mapped to same status keys)
    "Programme Basic Data": "Szakjelentés alapadatok",
    "The Programme": "Képzés célja",
    "Programme Curriculum": "Szak mintaterve",
    "Talented Students and Outstanding Student Performance": "Tehetséges hallgatók",
    "Foreign Language Requirements": "Idegen nyelvi követelmények",
    "Compulsory Professional Practice": "Kötelező szakmai gyakorlat",
    "Curriculum Developments and Modifications": "Mintatanterv fejlesztések, módosítások",
    "Development Recommendations": "Fejlesztési javaslatok",
    "Teaching": "Oktatás",
    "Data related to the major": "Szak létszám adatok",
    "Competitor Analysis": "Versenytárs elemzés",
    "Guest Lecturers and Part-time Teachers": "Meghívott előadók és óraadók",
    "Assessment of Teachers": "Oktatók értékelése",
    "Study Conditions": "Tanulmányi körülmények",
    "Programme Communication": "Szak kommunikációja",
    "Implementation and Evaluation of Development Recommendations": "Fejlesztési javaslatok megvalósítása és értékelése",
    "Finalisation of Programme Report": "Szakjelentés alapadatok"
  };

  const urls = [
    `/szakjelentesek/${szakId}/alapadatok`,
    `/szakjelentes_letrehozas/${szakId}/kepzes-celja`,
    `/szakjelentes_letrehozas/${szakId}/szak-mintaterve`,
    `/szakjelentes_letrehozas/${szakId}/tehetseges-hallgatok`,
    `/szakjelentes_letrehozas/${szakId}/idegen-nyelvi-korulmenyek`,
    `/szakjelentes_letrehozas/${szakId}/kotelezo-szakmai-gyakorlat`,
    `/szakjelentes_letrehozas/${szakId}/mintatanterv-fejlesztesek`,
    `/szakjelentes_letrehozas/${szakId}/fejlesztesi-javaslatok`,
    `/szakjelentes_letrehozas/${szakId}/oktatas`,
    `/szakjelentes_letrehozas/${szakId}/szak-letszam-adatok`,
    `/szakjelentes_letrehozas/${szakId}/versenytars-elemzes`,
    `/szakjelentes_letrehozas/${szakId}/meghivott-eloadok`,
    `/szakjelentes_letrehozas/${szakId}/oktatok-ertekelese`,
    `/szakjelentes_letrehozas/${szakId}/tanulmanyi-korulmenyek`,
    `/szakjelentes_letrehozas/${szakId}/szak-kommunikacioja`,
    `/szakjelentes_letrehozas/${szakId}/megvalositas-ertekelese`,
    `/szakjelentesek/${szakId}`
  ];

  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const statusData = await getSectionStatus(szakId);
        console.log("Fetched raw status data:", statusData); // Log only the raw backend response
        setStatuses(statusData);
      } catch (error) {
        console.error("Error fetching section statuses:", error);
      }
    };

    fetchStatuses();
  }, [szakId]);

  // Scroll the active item to the center of the sidebar when the page changes
  useEffect(() => {
    const contentElement = contentRef.current;
    if (contentElement) {
      const activeItem = contentElement.querySelector('.item.active');
      if (activeItem) {
        activeItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [location.pathname]);

  useEffect(() => {
    if (preview) return;
    // querySelectorAll-t használunk, hogy minden érintett dobozt megtaláljon
    const containers = document.querySelectorAll('.uj-szak-container, .details-container');
    containers.forEach(container => {
    if (isOpen) {
      container.classList.add('side-tile-open');
    } else {
      container.classList.remove('side-tile-open');
    }
  });
  }, [isOpen, preview]);

  const getStatusClassName = (text) => {
    // Force these sections to always be green (both languages)
    if (text === "Szak alapadatok" || text === "Programme Basic Data") {
      return "green";
    }

    const status = statuses[textToStatusKeyMap[text]];
    switch (status) {
      case 1:
        return "red";
      case 2:
        return "yellow";
      case 3:
        return "green";
      default:
        return "grey";
    }
  };

  const statusLabels = {
    red: 'Nincs kitöltve',
    yellow: 'Folyamatban',
    green: 'Kész',
    grey: 'Nincs adat'
  };

  const toggleSideTile = () => {
    setIsOpen(!isOpen);
  };

  const currentIndex = urls.indexOf(location.pathname);

  const handleNext = () => {
    if (currentIndex < urls.length - 1) {
      navigate(urls[currentIndex + 1]);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      navigate(urls[currentIndex - 1]);
    }
  };

  return (
    <>
      {isVisible && !preview && (  // Render a gombot csak akkor, ha a SideTile látható
        <button className={`toggle-button ${isOpen ? 'open' : 'closed'}`} onClick={toggleSideTile}>
          {isOpen ? '<' : '>'}
        </button>
      )}
      {isVisible && (  
        <div className={`side-tile ${preview ? 'preview' : isOpen ? 'open' : 'closed'}`}>
          <div className="content" ref={contentRef}>
            {texts.map((text, index) => {
              const statusClass = getStatusClassName(text);
              return (
                <div className={`item ${urls[index] === location.pathname ? 'active' : ''}`} key={index}>
                  <div className={`circle ${statusClass}`} role="img" aria-label={`${text}: ${statusLabels[statusClass]}`}></div>
                  {index < texts.length - 1 && <div className="connector"></div>}
                  {preview ? <p className="text">{text}</p> : (
                    <Link to={urls[index]} className="text-link">
                      <p className="text">{text}</p>
                    </Link>
                  )}
                </div>
              );
            })}
            {!preview && <div className="navigation-arrows">
              <button
                className="nav-arrow previous"
                onClick={handlePrevious}
                disabled={currentIndex <= 0}
              >
                &#8592;
              </button>
              <button
                className="nav-arrow next"
                onClick={handleNext}
                disabled={currentIndex >= urls.length - 1}
              >
                &#8594;
              </button>
            </div>}
          </div>
        </div>
      )}
    </>
  );
});

export default SideTile;
